/**
 * Internal booking (AGENTS.md section 11, Phase 2). Consultation types, doctor availability
 * and appointments. Times are stored in UTC; the clinic's and the patient's time zones are
 * kept explicitly so both sides see their own wall-clock time (11.4).
 */
import { z } from "zod";
import { e164Schema, hhmmSchema, timestampSchema } from "../common.js";
export const timeRangeSchema = z.object({ from: hhmmSchema, to: hhmmSchema });
export const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
export const weekdaySchema = z.enum(WEEKDAYS);
/** Weekly working hours plus dated overrides (an empty list on a date = day off). */
export const availabilitySchema = z.object({
    weekly: z.partialRecord(weekdaySchema, z.array(timeRangeSchema).max(6)).default({}),
    overrides: z
        .record(z.string().regex(/^\d{4}-\d{2}-\d{2}$/), z.array(timeRangeSchema).max(6))
        .default({}),
});
export const DEFAULT_AVAILABILITY = {
    weekly: {
        mon: [{ from: "09:00", to: "17:00" }],
        tue: [{ from: "09:00", to: "17:00" }],
        wed: [{ from: "09:00", to: "17:00" }],
        thu: [{ from: "09:00", to: "17:00" }],
        fri: [{ from: "09:00", to: "17:00" }],
    },
    overrides: {},
};
/**
 * `clinics/{id}/locations/{locationId}`: a place where in-person appointments happen (a clinic with
 * rooms on Harley Street and in St Albans has two). Owner request, 13 October 2026.
 */
export const locationSchema = z.object({
    name: z.string().min(1).max(80),
    address: z.string().min(1).max(300),
    /** WhatsApp number for people booked at this location; falls back to the clinic number or agent routing. */
    whatsappNumber: e164Schema.optional(),
    active: z.boolean().default(true),
    order: z.number().int().default(0),
    /** Own appointment hours here when the clinic assigns specialists later; missing = the clinic's. */
    hours: availabilitySchema.optional(),
    /** Appointments that can run at the same time here; missing = the clinic's setting. */
    capacity: z.number().int().min(1).max(50).optional(),
});
/**
 * How patients are booked (owner request, 15 October 2026). "later": against the clinic's
 * appointment hours, and the clinic assigns a specialist afterwards in the dashboard (the booking
 * page never asks for one). "specialists": against each specialist's working hours.
 */
export const bookingSettingsSchema = z.object({
    assign: z.enum(["later", "specialists"]).optional(),
    hours: availabilitySchema.optional(),
    capacity: z.number().int().min(1).max(50).optional(),
});
export const consultationModeSchema = z.enum(["online", "in_person"]);
/** 11.2. `price` is in minor units (pence); 0 = free. */
export const consultationTypeSchema = z.object({
    name: z.string().min(1).max(80),
    description: z.string().max(300).optional(),
    mode: consultationModeSchema,
    durationMinutes: z.number().int().min(5).max(240),
    price: z.number().int().min(0).max(1_000_000).default(0),
    currency: z.string().length(3).default("GBP"),
    /** Doctors who offer this type; empty = every active doctor. */
    doctorIds: z.array(z.string()).max(50).default([]),
    bufferMinutes: z.number().int().min(0).max(120).default(0),
    minNoticeHours: z.number().int().min(0).max(720).default(24),
    maxAdvanceDays: z.number().int().min(1).max(365).default(60),
    cancellationPolicyText: z.string().max(500).optional(),
    /** Where an in-person consultation takes place when the clinic has no locations; falls back to the clinic address. */
    address: z.string().max(300).optional(),
    /** Locations offering this in-person type; empty = every active location. */
    locationIds: z.array(z.string()).max(50).default([]),
    active: z.boolean().default(true),
    order: z.number().int().default(0),
});
export const APPOINTMENT_STATUSES = [
    "held",
    "confirmed",
    "cancelled",
    "completed",
    "no_show",
];
export const appointmentStatusSchema = z.enum(APPOINTMENT_STATUSES);
/** `clinics/{id}/appointments/{id}`. Created by the widget API (or a PA); no clinical content (D6). */
export const appointmentSchema = z.object({
    leadId: z.string(),
    leadRef: z.string(),
    consultationTypeId: z.string(),
    typeName: z.string().max(80),
    mode: consultationModeSchema,
    doctorId: z.string(),
    doctorName: z.string().max(120),
    startAt: timestampSchema,
    endAt: timestampSchema,
    /** Clinic time zone at booking time. */
    timezone: z.string().max(64),
    patientTimezone: z.string().max(64).optional(),
    status: appointmentStatusSchema,
    holdExpiresAt: timestampSchema.optional(),
    price: z.number().int().min(0).default(0),
    currency: z.string().length(3).default("GBP"),
    paymentStatus: z.enum(["none", "pending", "paid", "refunded"]).default("none"),
    /** PaymentIntent on the connected account (paid types). */
    paymentIntentId: z.string().optional(),
    paidAt: timestampSchema.optional(),
    refundedAt: timestampSchema.optional(),
    address: z.string().max(300).optional(),
    /** Chosen location for in-person appointments (`address` carries its address). */
    locationId: z.string().optional(),
    locationName: z.string().max(80).optional(),
    meeting: z
        .object({ provider: z.enum(["google_meet", "zoom", "manual"]), url: z.string().max(2048) })
        .optional(),
    /** Event written to the specialist's Google calendar (11.4). */
    googleEventId: z.string().max(200).optional(),
    googleCalendarId: z.string().max(300).optional(),
    /** First name and last initial for lists; the lead holds the full contact. */
    patientLabel: z.string().max(80).optional(),
    /** Procedure names chosen by staff for a manual appointment (owner request, 14 October 2026). */
    procedures: z.array(z.string().max(120)).max(20).optional(),
    internalNotes: z.string().max(2000).optional(),
    bookedBy: z.enum(["patient", "staff"]).default("patient"),
    /** Staff member who created or last moved it (manual bookings). */
    createdBy: z.string().optional(),
    /** Set when the 24-hour reminder went out. Cleared on reschedule. */
    reminderSentAt: timestampSchema.optional(),
    cancelledAt: timestampSchema.optional(),
    cancelReason: z.string().max(200).optional(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
/** Fields a PA may change on an appointment from the dashboard (everything else is server-owned). */
export const APPOINTMENT_PA_EDITABLE_FIELDS = [
    "status",
    "cancelledAt",
    "cancelReason",
    "internalNotes",
    "meeting",
    "updatedAt",
];
/**
 * Appointments booked against the clinic's hours sit in an unassigned pool until the clinic
 * assigns a specialist: `unassigned` (online, or no locations) or `unassigned:<locationId>`.
 */
export const UNASSIGNED_ID = "unassigned";
export const unassignedId = (locationId) => locationId ? `${UNASSIGNED_ID}:${locationId}` : UNASSIGNED_ID;
export const isUnassigned = (doctorId) => Boolean(doctorId) && (doctorId === UNASSIGNED_ID || doctorId.startsWith(`${UNASSIGNED_ID}:`));
//# sourceMappingURL=booking.js.map